import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, RefreshCw, Search } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "nandaSA.adminToken";

export default function AdminLeads() {
 const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
 const [inputToken, setInputToken] = useState("");
 const [authError, setAuthError] = useState("");
 const [leads, setLeads] = useState(null);
 const [loading, setLoading] = useState(false);
 const [query, setQuery] = useState("");

 useEffect(() => {
 document.title = "NANDA SÁ — Admin · Prospects";
 }, []);

 const fetchLeads = React.useCallback(async (tok) => {
 setLoading(true);
 try {
 const res = await fetch(`${API_URL}/api/leads`, {
 headers: { "X-Admin-Token": tok },
 });
 if (res.status === 401) {
 localStorage.removeItem(STORAGE_KEY);
 setToken("");
 setAuthError("Token invalide.");
 return;
 }
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 const data = await res.json;
 setLeads(data);
 } catch {
 setAuthError("Erreur réseau.");
 } finally {
 setLoading(false);
 }
 }, []);

 useEffect(() => {
 if (token) fetchLeads(token);
 }, [token, fetchLeads]);

 const onSubmit = async (e) => {
 e.preventDefault;
 setAuthError("");
 try {
 const res = await fetch(`${API_URL}/api/admin/verify`, {
 method: "POST",
 headers: { "X-Admin-Token": inputToken },
 });
 if (!res.ok) {
 setAuthError("Token invalide.");
 return;
 }
 localStorage.setItem(STORAGE_KEY, inputToken);
 setToken(inputToken);
 setInputToken("");
 } catch {
 setAuthError("Erreur réseau.");
 }
 };

 const onLogout = () => {
 localStorage.removeItem(STORAGE_KEY);
 setToken("");
 setLeads(null);
 };

 const filtered = React.useMemo(() => {
 if (!leads) return [];
 const q = query.trim.toLowerCase;
 if (!q) return leads;
 return leads.filter((l) =>
 [l.first_name, l.last_name, l.email, l.country, l.company, l.locale, l.source, l.interest].filter(Boolean).some((v) => String(v).toLowerCase.includes(q))
 );
 }, [leads, query]);

 const exportCsv = async () => {
 const res = await fetch(`${API_URL}/api/leads.csv`, {
 headers: { "X-Admin-Token": token },
 });
 if (!res.ok) return;
 const blob = await res.blob;
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url;
 a.download = `nanda-sa-leads-${new Date().toISOString().slice(0, 10)}.csv`;
 document.body.appendChild(a);
 a.click();
 document.body.removeChild(a);
 URL.revokeObjectURL(url);
 };

 if (!token) {
 return (
 <div className="min-h-screen bg-nanda-bg" data-testid="admin-login">
 <div className="mx-auto max-w-md px-6 py-32">
 <Link to="/" className="label-overline text-nanda-muted hover:text-nanda-ink transition-colors">← NANDA SÁ</Link>
 <h1 className="mt-10 font-serif text-4xl text-nanda-ink">Admin · Prospects</h1>
 <p className="mt-4 text-nanda-inkSoft text-sm leading-relaxed">
 Accès restreint. Saisissez votre jeton d&apos;administration pour consulter la base de prospects capturés par le site.
 </p>
 <form onSubmit={onSubmit} className="mt-10 space-y-6">
 <div>
 <label htmlFor="admin-token" className="label-overline text-nanda-muted block mb-3">
 Jeton d&apos;administration
 </label>
 <input
 id="admin-token"
 type="password"
 value={inputToken}
 onChange={(e) => setInputToken(e.target.value)}
 required
 autoComplete="current-password"
 data-testid="admin-token-input"
 className="w-full border-b border-nanda-line bg-transparent py-3 text-nanda-ink focus:outline-none focus:border-nanda-ink transition-colors"
 />
 </div>
 {authError && (
 <div data-testid="admin-error" className="text-nanda-terracotta text-sm">
 {authError}
 </div>
 )}
 <button
 type="submit"
 data-testid="admin-submit"
 className="inline-flex items-center gap-3 bg-nanda-ink text-white px-7 py-3 hover:bg-nanda-terracotta transition-colors duration-500 label-overline"
 >
 Se connecter
 </button>
 </form>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-nanda-bg" data-testid="admin-dashboard">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-16 md:py-20">
 <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
 <div>
 <Link to="/" className="label-overline text-nanda-muted hover:text-nanda-ink transition-colors">
 ← Retour au site
 </Link>
 <h1 className="mt-4 font-serif text-4xl md:text-5xl text-nanda-ink">Prospects capturés</h1>
 <p className="mt-3 text-nanda-inkSoft text-sm">
 {loading
 ? "Chargement…"
 : leads === null
 ? "—"
 : `${leads.length} prospect${leads.length > 1 ? "s" : ""} enregistré${leads.length > 1 ? "s" : ""}`}
 </p>
 </div>
 <div className="flex flex-wrap items-center gap-3">
 <button
 onClick={() => fetchLeads(token)}
 data-testid="admin-refresh"
 className="inline-flex items-center gap-2 border border-nanda-line px-4 py-2 hover:bg-nanda-bgAlt transition-colors label-overline"
 >
 <RefreshCw size={14} strokeWidth={1.5} className={loading ? "animate-spin" : ""} />
 Actualiser
 </button>
 <button
 onClick={exportCsv}
 data-testid="admin-export"
 className="inline-flex items-center gap-2 bg-nanda-ink text-white px-4 py-2 hover:bg-nanda-terracotta transition-colors label-overline"
 >
 <Download size={14} strokeWidth={1.5} />
 Export CSV
 </button>
 <button
 onClick={onLogout}
 data-testid="admin-logout"
 className="inline-flex items-center gap-2 border border-nanda-line px-4 py-2 hover:bg-nanda-bgAlt transition-colors label-overline"
 >
 <LogOut size={14} strokeWidth={1.5} />
 Déconnexion
 </button>
 </div>
 </div>

 <div className="relative mb-8 max-w-md">
 <Search size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-nanda-muted" />
 <input
 type="search"
 placeholder="Rechercher (nom, email, pays, société)…"
 value={query}
 onChange={(e) => setQuery(e.target.value)}
 data-testid="admin-search"
 className="w-full pl-6 border-b border-nanda-line bg-transparent py-3 text-nanda-ink focus:outline-none focus:border-nanda-ink transition-colors"
 />
 </div>

 <div className="border border-nanda-line overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-nanda-bgAlt label-overline text-nanda-muted">
 <th className="text-left p-4">Date</th>
 <th className="text-left p-4">Prénom</th>
 <th className="text-left p-4">Nom</th>
 <th className="text-left p-4">E-mail</th>
 <th className="text-left p-4">Pays</th>
 <th className="text-left p-4">Société</th>
 <th className="text-left p-4">Langue</th>
 <th className="text-left p-4">Source</th>
 </tr>
 </thead>
 <tbody>
 {filtered.length === 0 && !loading && (
 <tr>
 <td colSpan={8} className="p-8 text-center text-nanda-muted">
 Aucun prospect{query ? " ne correspond à cette recherche" : " enregistré pour le moment"}.
 </td>
 </tr>
 )}
 {filtered.map((l) => (
 <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-nanda-line hover:bg-nanda-sand/30">
 <td className="p-4 text-nanda-inkSoft whitespace-nowrap">
 {new Date(l.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
 </td>
 <td className="p-4 text-nanda-ink">{l.first_name}</td>
 <td className="p-4 text-nanda-ink font-medium">{l.last_name}</td>
 <td className="p-4">
 <a href={`mailto:${l.email}`} className="text-nanda-ink hover:text-nanda-terracotta transition-colors">
 {l.email}
 </a>
 </td>
 <td className="p-4 text-nanda-inkSoft">{l.country}</td>
 <td className="p-4 text-nanda-inkSoft">{l.company || "—"}</td>
 <td className="p-4 text-nanda-muted uppercase text-xs">{l.locale}</td>
 <td className="p-4 text-nanda-muted text-xs">{l.source}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 </div>
 );
}
